import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApplyColumnStateParams, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { TaxAuthority } from '../../../Assessor-Collector/Tax-Rates/tax.rates.panel.model';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../../Compliance/AgGrid';
import { WsAgGridWrapperComponent } from '../../../Compliance/AgGrid/AgGridWrapper/wsAgGridWrapper.component';
import {
    AgGridMultiSelectedCellRenderer,
    AgGridMultiSelectedHeaderRenderer,
    AgGridMultiSelectRendererParams,
    AgGridMultiSelectTracker,
} from '../../../Compliance/AgGrid/MultiSelectTracker';
import { IWeissmanModalComponent } from '../../../Compliance/WeissmanModalService';
import { ModalWrapperComponent } from '../../../UI-Lib/Modal-Wrapper/modalWrapper.component';

export interface BulkTaxAuthorityAddComponentParams {
    taxAuthorities: TaxAuthority[];
}

@Component({
    selector: 'bulk-assessor-add',
    templateUrl: './bulkTaxAuthorityAdd.component.html',
    imports: [WsAgGridWrapperComponent, ModalWrapperComponent],
})
export class BulkTaxAuthorityAddComponent
    implements OnInit, OnDestroy, IWeissmanModalComponent<BulkTaxAuthorityAddComponentParams, TaxAuthority[]>
{
    constructor(private readonly _bsModalRef: BsModalRef) {}

    params: BulkTaxAuthorityAddComponentParams;
    result: TaxAuthority[];

    gridTracker: AgGridMultiSelectTracker;

    gridOptions: GridOptions = new AgGridOptionsBuilder({
        onFilterChanged: () => this.gridTracker.onGridFilterChanged(),
        onSortChanged: () => this.gridTracker.onGridSortChanged(),
    })
        .withContext(this)
        .withLoadingOverlay()
        .withColumnResize()
        .withSort()
        .withTextSelection()
        .withFloatingFilter()
        .build();

    private _gridApi: GridApi;
    private _taxAuthorities: TaxAuthority[];
    private _selectedRowsSub: Subscription;

    async ngOnInit(): Promise<void> {
        this._taxAuthorities = this.params.taxAuthorities || [];
        this._setRowData();
    }

    ngOnDestroy(): void {
        this._selectedRowsSub && this._selectedRowsSub.unsubscribe();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        this.gridTracker = new AgGridMultiSelectTracker(
            this.gridOptions,
            this._gridApi,
            this._getGridRowIds.bind(this),
        );

        this._selectedRowsSub = this.gridTracker.selectedRows$.subscribe((selected: Compliance.SelectedRowsModel) => {
            this.result = selected.selectAllRows
                ? this._taxAuthorities.filter((x) => !selected.selectedRows.includes(x.taxAuthorityId))
                : this._taxAuthorities.filter((x) => selected.selectedRows.includes(x.taxAuthorityId));
        });

        const columns: ColDef[] = [
            {
                field: 'taxAuthorityId',
                width: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                resizable: false,
                suppressMovable: true,
                suppressColumnsToolPanel: true,
                pinned: 'left',
                lockPinned: true,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: {
                    tracker: this.gridTracker,
                } as AgGridMultiSelectRendererParams,
                cellRenderer: AgGridMultiSelectedCellRenderer,
                cellRendererParams: {
                    tracker: this.gridTracker,
                } as AgGridMultiSelectRendererParams,
            },
            {
                headerName: 'Name',
                field: 'name',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Code',
                field: 'code',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [
                { colId: 'collector', sort: 'asc' },
                { colId: 'name', sort: 'asc' },
                { colId: 'code', sort: 'asc' },
            ],
            defaultState: { sort: null },
        };

        this._gridApi.setGridOption('columnDefs', columns);
        this._gridApi.sizeColumnsToFit();
        this._gridApi.applyColumnState(defaultSortModel);
        this._setRowData();
    }

    save(): void {
        this._bsModalRef.hide();
    }

    cancel(): void {
        this.result = null;
        this._bsModalRef.hide();
    }

    private async _getGridRowIds(skip, take): Promise<Compliance.QueryResultModel<number>> {
        const model = { rowsToDisplay: [] };
        this._gridApi.forEachNode((x) => model.rowsToDisplay.push(x));
        const rows = model.rowsToDisplay.slice(skip, take + 1);
        return {
            lastModifiedTimestamp: new Date(),
            totalRows: rows.length,
            totalValidRows: rows.length,
            data: rows.map((x) => {
                const taxAuthority = x.data as TaxAuthority;
                return taxAuthority && taxAuthority.taxAuthorityId;
            }),
        };
    }

    private _setRowData(): void {
        if (!(this._gridApi && this._taxAuthorities)) {
            return;
        }

        this.gridTracker && this.gridTracker.clear();

        this._gridApi.setGridOption('rowData', this._taxAuthorities);
    }
}
